import { z } from 'zod';

/**
 * Workflow definitions are DATA (ADR-011): versioned compositions of registered step types.
 * Arguments may reference `${input.<path>}`, `${steps.<key>.output.<path>}` and
 * `${config.<path>}`; conditions use a JSONLogic subset evaluated without `eval`.
 * No model names, connection secrets or vendor specifics belong in a definition.
 */
export const JsonLogic: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonLogic),
    z.record(z.string(), JsonLogic),
  ]),
);

export const WorkflowStep = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  type: z.string().regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/),
  description: z.string().min(1).max(500),
  /** Step parameters; values may contain `${...}` references. */
  with: z.record(z.string(), z.unknown()).optional(),
  /** Run the step only when this JSONLogic condition is truthy (otherwise SKIPPED). */
  when: JsonLogic.optional(),
});
export type WorkflowStep = z.infer<typeof WorkflowStep>;

export const WorkflowDefinition = z.object({
  key: z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/),
  version: z.number().int().positive(),
  domain: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string().min(1).max(120),
  description: z.string().min(1).max(1000),
  /** Named input schema, e.g. "ea.AnalyseIssueInput@1" (resolved by the schema registry). */
  inputSchema: z.string().regex(/^[a-z][a-z0-9]*(\.[A-Za-z0-9_]+)+@\d+$/),
  /** Minimum clearance rank to run it (0 PUBLIC … 3 RESTRICTED). */
  classificationRank: z.number().int().min(0).max(3),
  steps: z.array(WorkflowStep).min(1).max(50),
  /** Non-secret defaults referenced as `${config.<key>}` (e.g. which connection to use). */
  config: z.record(z.string(), z.unknown()).optional(),
  /** Result mapping: each value is a `${...}` reference or a literal. */
  output: z.record(z.string(), z.unknown()).optional(),
  /** Publish as a dedicated MCP tool (in addition to execute_workflow). */
  exposeAsTool: z
    .object({
      name: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
      title: z.string().min(1).max(120),
      description: z.string().min(1).max(1000),
    })
    .optional(),
});
export type WorkflowDefinition = z.infer<typeof WorkflowDefinition>;

/** Result returned by a guard that stops a workflow (e.g. a failed prerequisite). */
export const StoppedResult = z.object({
  stopped: z.literal(true),
  stepKey: z.string(),
  reason: z.string(),
  details: z.record(z.string(), z.unknown()),
});
export type StoppedResult = z.infer<typeof StoppedResult>;
